/**
 * Removes query parameters from a URL
 *
 * @param url - The URL to process (can be relative or absolute)
 * @param paramsToRemove - Optional array of specific query parameter names to remove.
 *                         If not provided, all query parameters will be removed.
 * @returns The URL with specified query parameters removed
 *
 * @example
 * removeUrlQueryParams('https://example.com/page?foo=bar&baz=qux')
 * // Returns: 'https://example.com/page'
 *
 * @example
 * removeUrlQueryParams('https://example.com/page?foo=bar&baz=qux', ['foo'])
 * // Returns: 'https://example.com/page?baz=qux'
 *
 * @example
 * removeUrlQueryParams('/page?foo=bar')
 * // Returns: '/page'
 */
export const removeUrlQueryParams = (url?: string, paramsToRemove?: string[]): string | undefined => {
  if (!url) {
    return undefined;
  }

  try {
    // Check if it's a valid URL by trying to parse it
    let urlObj: URL;
    let isAbsolutePath = false;
    let isProtocolRelative = false;

    const protocolPlaceholder = "https:";
    const domainPlaceholder = "https://placeholder.com";

    if (url.startsWith("http://") || url.startsWith("https://")) {
      urlObj = new URL(url);
    } else if (url.startsWith("//")) {
      // Protocol-relative URL
      isProtocolRelative = true;
      urlObj = new URL(`${protocolPlaceholder}${url}`);
    } else {
      // Absolute path
      isAbsolutePath = true;
      urlObj = new URL(url, domainPlaceholder);
    }

    // Remove query parameters
    if (paramsToRemove) {
      paramsToRemove.forEach((param) => {
        urlObj.searchParams.delete(param);
      });
    } else {
      urlObj.search = "";
    }

    // Reconstruct the URL
    let resultUrl = urlObj.toString();

    // Handle protocol-relative URLs (remove placeholder protocol)
    if (isProtocolRelative) {
      resultUrl = resultUrl.replace(protocolPlaceholder, "");
    }

    // Handle absolute paths (remove placeholder domain)
    if (isAbsolutePath) {
      resultUrl = resultUrl.replace(domainPlaceholder, "");
    }

    return resultUrl;
  } catch {
    // If URL parsing fails, return the original URL
    // This handles edge cases with malformed URLs
    return url;
  }
};
